import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Modal,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { doc, onSnapshot, query, setDoc, deleteDoc } from "firebase/firestore";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";

// Modal to add/edit item categories
const CategoryModal = ({ onClose, editingCategory, categories }) => {
  const [categoryName, setCategoryName] = useState("");
  const [displayInDownRoomsList, setDisplayInDownRoomsList] = useState(true);
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  useEffect(() => {
    if (editingCategory) {
      setCategoryName(editingCategory.name || editingCategory.id || "");
      setDisplayInDownRoomsList(editingCategory.displayInDownRoomsList !== false);
    }
  }, [editingCategory]);

  const handleSave = async () => {
    if (!categoryName.trim()) {
      alert("Category name is required");
      return;
    }

    const itemCategoriesRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "itemCatagories"
    );
    const itemCategoryDoc = doc(itemCategoriesRef, categoryName.trim());

    await setDoc(itemCategoryDoc, {
      name: categoryName.trim(),
      displayInDownRoomsList: displayInDownRoomsList,
    });

    onClose();
  };

  return (
    <Modal open={true} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "auto",
          minWidth: "30rem",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 2,
          outline: "none",
        }}
      >
        <Paper elevation={3} style={{ padding: "20px" }}>
          <Typography variant="h6" gutterBottom>
            {editingCategory ? "Edit Category" : "Add New Category"}
          </Typography>

          <TextField
            label="Category Name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            fullWidth
            margin="normal"
            disabled={!!editingCategory} // Disable editing name for existing categories
          />

          <FormControlLabel
            control={
              <Switch
                checked={displayInDownRoomsList}
                onChange={(e) => setDisplayInDownRoomsList(e.target.checked)}
                color="primary"
              />
            }
            label="Display in Down Rooms List"
            sx={{ mt: 2, mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When disabled, items in this category will not appear in the rooms
            grid or room detail views.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button variant="contained" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              {editingCategory ? "Update Category" : "Add Category"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

function ManageItemCategories() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  useEffect(() => {
    setLoading(true);

    // Fetch item categories
    const itemCategoriesRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "itemCatagories"
    );
    const itemCategoriesQuery = query(itemCategoriesRef);
    const unsubscribeCategories = onSnapshot(
      itemCategoriesQuery,
      (querySnapshot) => {
        const categoriesArray = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          categoriesArray.push({
            id: doc.id,
            name: data.name || doc.id,
            displayInDownRoomsList: data.displayInDownRoomsList !== false,
          });
        });
        setCategories(categoriesArray);
      }
    );

    // Fetch items to count per category
    const itemsRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "items"
    );
    const itemsQuery = query(itemsRef);
    const unsubscribeItems = onSnapshot(itemsQuery, (querySnapshot) => {
      const itemsArray = [];
      querySnapshot.forEach((doc) => {
        itemsArray.push(doc.data());
      });
      setItems(itemsArray);
      setLoading(false);
    });

    return () => {
      unsubscribeCategories();
      unsubscribeItems();
    };
  }, [selectedProperty]);

  // Count items per category
  const getCategoryItemCount = (categoryName) => {
    return items.filter((item) => item.itemType === categoryName).length;
  };

  // Handle delete category
  const handleDeleteClick = (category, event) => {
    event.stopPropagation(); // Prevent row click
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      const categoryRef = propertyDoc(
        selectedClient,
        selectedProperty,
        "itemCatagories",
        categoryToDelete.id
      );
      await deleteDoc(categoryRef);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error("Error deleting category:", error);
      alert("Error deleting category. Please try again.");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const columns = [
    { field: "name", headerName: "Category Name", flex: 1 },
    {
      field: "itemCount",
      headerName: "Items",
      width: 100,
      valueGetter: (params) => getCategoryItemCount(params.row.name),
    },
    {
      field: "displayInDownRoomsList",
      headerName: "Show in Down Rooms",
      width: 180,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value ? "success.main" : "text.disabled",
            fontWeight: params.value ? "bold" : "normal",
          }}
        >
          {params.value ? "Yes" : "No"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={(event) => handleDeleteClick(params.row, event)}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h5" gutterBottom>
                Manage Item Categories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure which item categories appear in down rooms lists
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setEditingCategory(null);
                setCategoryModalOpen(true);
              }}
            >
              Add Category
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={categories}
              columns={columns}
              autoHeight
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowId={(row) => row.id}
              onRowClick={(params) => {
                setEditingCategory(params.row);
                setCategoryModalOpen(true);
              }}
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
              }}
            />
          )}

          {categoryModalOpen && (
            <CategoryModal
              onClose={() => {
                setCategoryModalOpen(false);
                setEditingCategory(null);
              }}
              editingCategory={editingCategory}
              categories={categories}
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Category "{categoryToDelete?.name}"?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {categoryToDelete && getCategoryItemCount(categoryToDelete.name) > 0 ? (
              <>
                <strong>Warning:</strong> This category has{" "}
                {getCategoryItemCount(categoryToDelete.name)} item(s) assigned to it.
                <br />
                <br />
                Please reassign these items to a different category before deleting, or they will no longer have a category assignment.
                <br />
                <br />
                Are you sure you want to delete this category?
              </>
            ) : (
              <>
                Are you sure you want to delete this category? This action cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ManageItemCategories;
